import { useState, useCallback, useRef, useEffect } from "react";

// Extend Window interface for Web Speech API
interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message?: string;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  0: SpeechRecognitionAlternative;
  length: number;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onstart: (() => void) | null;
  onspeechend: (() => void) | null;
  onnomatch: (() => void) | null;
  onaudiostart: (() => void) | null;
  onaudioend: (() => void) | null;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognitionInstance;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

interface UseSpeechRecognitionOptions {
  onResult?: (transcript: string) => void;
  onEnd?: () => void;
  onError?: (error: string) => void;
  continuous?: boolean;
  lang?: string;
}

export function useSpeechRecognition({ 
  onResult, 
  onEnd, 
  onError,
  continuous = true, 
  lang = "en-US" 
}: UseSpeechRecognitionOptions = {}) {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState<string | null>(null);
  
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const isListeningRef = useRef(false);
  const shouldRestartRef = useRef(false);
  
  // Keep callbacks in refs to avoid recreation
  const onResultRef = useRef(onResult);
  const onEndRef = useRef(onEnd);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onResultRef.current = onResult;
    onEndRef.current = onEnd;
    onErrorRef.current = onError;
  }, [onResult, onEnd, onError]);

  // Check support on mount
  useEffect(() => {
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    const supported = !!SpeechRecognitionAPI;
    setIsSupported(supported);
    console.log("[SpeechRecognition] Supported:", supported, "API:", SpeechRecognitionAPI ? "available" : "missing");
    
    return () => {
      shouldRestartRef.current = false;
      isListeningRef.current = false;
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {
          // Ignore abort errors
        }
      }
    };
  }, []);

  // CRITICAL: Create and start recognition directly from user gesture
  // This function must be called synchronously from a click/tap handler
  const startListening = useCallback(() => {
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognitionAPI) {
      console.log("[SpeechRecognition] Not supported");
      setError("Speech recognition not supported in this browser");
      return;
    }
    
    if (isListeningRef.current) {
      console.log("[SpeechRecognition] Already listening");
      return;
    }

    console.log("[SpeechRecognition] Creating new instance and starting...");
    
    // Always create a fresh instance - required for iOS Safari
    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = continuous;
    recognition.interimResults = true;
    recognition.lang = lang;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      console.log("[SpeechRecognition] ✓ Started successfully");
      setIsListening(true);
      setError(null);
    };

    recognition.onaudiostart = () => {
      console.log("[SpeechRecognition] ✓ Audio capture started");
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = "";
      let interimTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interimTranscript += result[0].transcript;
        }
      }

      const currentTranscript = finalTranscript || interimTranscript;
      setTranscript(currentTranscript);
      console.log("[SpeechRecognition] Transcript:", currentTranscript);

      if (finalTranscript) {
        console.log("[SpeechRecognition] Final:", finalTranscript);
        onResultRef.current?.(finalTranscript.trim());
      }
    };

    recognition.onspeechend = () => {
      console.log("[SpeechRecognition] Speech ended");
    };

    recognition.onnomatch = () => {
      console.log("[SpeechRecognition] No speech match");
    };

    recognition.onaudioend = () => {
      console.log("[SpeechRecognition] Audio capture ended");
    };

    recognition.onend = () => {
      console.log("[SpeechRecognition] Session ended, shouldRestart:", shouldRestartRef.current);
      setIsListening(false);
      
      // Auto-restart if we should keep listening (handles silence timeout)
      if (shouldRestartRef.current && isListeningRef.current) {
        console.log("[SpeechRecognition] Auto-restarting...");
        setTimeout(() => {
          if (shouldRestartRef.current && isListeningRef.current && recognitionRef.current) {
            try {
              recognitionRef.current.start();
            } catch (e) {
              console.error("[SpeechRecognition] Restart failed:", e);
              // Try creating a new instance
              isListeningRef.current = false;
              shouldRestartRef.current = false;
            }
          }
        }, 100);
      } else {
        onEndRef.current?.();
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error("[SpeechRecognition] Error:", event.error, event.message);
      
      switch (event.error) {
        case "not-allowed":
          setError("Microphone permission denied. Please allow microphone access in Settings.");
          shouldRestartRef.current = false;
          isListeningRef.current = false;
          onErrorRef.current?.("Microphone permission denied");
          break;
        case "no-speech":
          // Normal timeout on mobile, will auto-restart via onend
          console.log("[SpeechRecognition] No speech detected (normal timeout)");
          break;
        case "aborted":
          // User or system aborted, don't restart
          console.log("[SpeechRecognition] Aborted");
          shouldRestartRef.current = false;
          break;
        case "network":
          setError("Network error. Speech recognition requires internet.");
          onErrorRef.current?.("Network error");
          break;
        case "audio-capture":
          setError("No microphone found or microphone is in use.");
          shouldRestartRef.current = false;
          onErrorRef.current?.("No microphone");
          break;
        case "service-not-allowed":
          setError("Speech recognition service not allowed. Check browser settings.");
          shouldRestartRef.current = false;
          onErrorRef.current?.("Service not allowed");
          break;
        default:
          setError(`Error: ${event.error}`);
          onErrorRef.current?.(event.error);
      }
      
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    setTranscript("");
    setError(null);
    isListeningRef.current = true;
    shouldRestartRef.current = true;

    // Start immediately - must be synchronous with user gesture
    try {
      recognition.start();
      console.log("[SpeechRecognition] start() called");
    } catch (e) {
      console.error("[SpeechRecognition] Start failed:", e);
      setError("Failed to start speech recognition");
      isListeningRef.current = false;
      shouldRestartRef.current = false;
    }
  }, [continuous, lang]);

  const stopListening = useCallback(() => {
    console.log("[SpeechRecognition] Stopping...");
    shouldRestartRef.current = false;
    isListeningRef.current = false;
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        console.error("[SpeechRecognition] Stop failed:", e);
      }
    }
    setIsListening(false);
  }, []);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  return {
    isListening,
    isSupported,
    transcript,
    error,
    startListening,
    stopListening,
    toggleListening,
  };
}
